import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Dropdown,
  Empty,
  Flex,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  Eye,
  MoreHorizontal,
  Plus,
  RotateCw,
  ShieldCheck,
  UserMinus
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  applicationsApplicationsAccessListOptions,
  type ApplicationAccessList
} from 'src/api/main'
import {
  ProTableContainer,
  SourceTag,
  TableActionColumnTitle,
  UserCell
} from 'src/components'
import {
  useHasWorkspacePermissions,
  useQueryParams,
  useTableSorter
} from 'src/hooks'
import { paths } from 'src/router'
import {
  defaultFilters,
  useShowApiErrors,
  WorkspacePermissionCode
} from 'src/utils'

import { useAppAccessRemoval } from '../hooks/useAppAccessRemoval'
import { appRoleIdSet, rolesOwnedByApp } from '../utils/appAccess'

import { AppAccessModal } from './AppAccessModal'

const { Text } = Typography

type Props = {
  applicationId: number
  organizationId: number
  /** `Application.related_roles` — the roles this app owns, used to scope the
   *  member roles shown per row. */
  relatedRoles?: { id: number; name: string }[]
}

export const AppUsersPanel = ({
  applicationId,
  organizationId,
  relatedRoles
}: Props) => {
  const { t } = useTranslation()
  const canAssignAppAccess = useHasWorkspacePermissions(
    WorkspacePermissionCode.APPLICATIONS_ACCESS
  )
  const appRoleIds = useMemo(() => appRoleIdSet(relatedRoles), [relatedRoles])
  const navigate = useNavigate()
  const { modal, message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { removeAccess } = useAppAccessRemoval(applicationId, organizationId)

  const params = useQueryParams()
  const sorter = useTableSorter()
  const [accessModal, setAccessModal] = useState<{
    open: boolean
    target: ApplicationAccessList | null
  }>({ open: false, target: null })

  const handleRemove = (record: ApplicationAccessList) => {
    modal.confirm({
      title: t('workspace.apps.usersTab.removeConfirmTitle'),
      content: t('workspace.apps.usersTab.removeConfirmBody'),
      okText: t('workspace.apps.usersTab.removeAccess'),
      okButtonProps: { danger: true },
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          await removeAccess(record)
          message.success(t('workspace.apps.usersTab.removeSuccess'))
        } catch (error) {
          showApiErrors(error)
        }
      }
    })
  }

  const goToUser = (userId: number) => {
    navigate(
      paths.workspaces.membersPerson(String(organizationId), String(userId))
    )
  }

  const enabled = applicationId > 0 && organizationId > 0

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...applicationsApplicationsAccessListOptions({
      query: {
        ...defaultFilters,
        ...params,
        application: applicationId,
        organization: organizationId
      }
    }),
    enabled
  })

  const rows = data?.results ?? []
  const total = data?.count ?? 0

  const columns: ColumnsType<ApplicationAccessList> = [
    {
      title: t('workspace.apps.usersTab.columns.user'),
      key: 'user__first_name',
      fixed: 'left',
      dataIndex: ['user', 'first_name'],
      sorter: true,
      render: (_, record) => (
        <UserCell
          firstName={record.user.first_name}
          lastName={record.user.last_name}
          email={record.user.email}
          photoUrl={record.user.photo?.file ?? null}
          size="md"
          onClick={() => goToUser(record.user.id)}
        />
      )
    },
    {
      title: t('workspace.apps.usersTab.columns.roles'),
      key: 'roles',
      width: 240,
      render: (_, record) => {
        if (record.role) {
          return (
            <Tag
              color="processing"
              style={{ marginInlineEnd: 0, fontSize: 11 }}
            >
              {record.role}
            </Tag>
          )
        }
        const scoped = rolesOwnedByApp(record.roles, appRoleIds)
        if (scoped.length === 0) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.usersTab.noRoles')}
            </Text>
          )
        }

        return (
          <Space size={[4, 4]} wrap>
            {scoped.map((role) => (
              <Tag
                key={role.id}
                color="processing"
                style={{ marginInlineEnd: 0, fontSize: 11 }}
              >
                {role.name}
              </Tag>
            ))}
          </Space>
        )
      }
    },
    {
      title: t('workspace.apps.usersTab.columns.source'),
      key: 'source',
      width: 100,
      render: (_, record) => <SourceTag ssoId={record.user.sso_id} />
    },
    {
      title: t('workspace.apps.usersTab.columns.userId'),
      key: 'userId',
      width: 110,
      dataIndex: ['user', 'id'],
      render: (id: number) => (
        <Text
          code
          copyable={{ tooltips: false }}
          style={{
            fontSize: 11,
            background: 'var(--ant-color-fill-tertiary)'
          }}
        >
          {String(id)}
        </Text>
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      align: 'right',
      width: 72,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'view',
                icon: <Eye size={14} />,
                label: t('users.actions.view'),
                onClick: () => goToUser(record.user.id)
              },
              {
                key: 'role',
                icon: <ShieldCheck size={14} />,
                label: t('users.actions.changeRole'),
                onClick: () => setAccessModal({ open: true, target: record })
              },
              { type: 'divider' as const },
              {
                key: 'remove',
                icon: <UserMinus size={14} />,
                label: t('workspace.apps.usersTab.removeAccess'),
                danger: true,
                onClick: () => handleRemove(record)
              }
            ]
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreHorizontal size={14} />}
            aria-label={t('users.actions.more')}
          />
        </Dropdown>
      )
    }
  ]

  return (
    <Flex vertical gap={14}>
      {isError ? (
        <Alert
          type="error"
          showIcon
          title={t('workspace.apps.loadError')}
          action={
            <Button size="small" onClick={() => void refetch()}>
              {t('workspace.apps.retry')}
            </Button>
          }
        />
      ) : null}

      <ProTableContainer<ApplicationAccessList>
        tableIdentifier="app-users"
        searchPlaceholder={t('workspace.apps.usersTab.searchPlaceholder')}
        rowKey="id"
        loading={isLoading}
        data={{ count: total, results: rows }}
        columns={columns}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        scroll={{ x: 880 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                params.search
                  ? t('workspace.apps.usersTab.empty.filtered')
                  : t('workspace.apps.usersTab.empty.none')
              }
            />
          )
        }}
        rightChildren={
          <Flex align="center" gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.usersTab.totalUsers', { count: total })}
            </Text>
            <Tooltip title={t('workspace.apps.usersTab.refresh')}>
              <Button
                size="small"
                type="text"
                icon={<RotateCw size={14} />}
                onClick={() => void refetch()}
                loading={isFetching}
              />
            </Tooltip>
            {canAssignAppAccess ? (
              <Button
                size="small"
                type="primary"
                icon={<Plus size={14} />}
                onClick={() => setAccessModal({ open: true, target: null })}
              >
                {t('workspace.apps.usersTab.grantAccess')}
              </Button>
            ) : null}
          </Flex>
        }
      />

      <AppAccessModal
        open={accessModal.open}
        applicationId={applicationId}
        organizationId={organizationId}
        manageTarget={accessModal.target}
        onClose={() => setAccessModal({ open: false, target: null })}
      />
    </Flex>
  )
}
